/** Section — live-game automation (Phase 1). Super admin only.
 *
 *  The primary control is "pick tonight's game". The team-follow box is a
 *  shortcut, not the main path. Every feed call is made by the backend, so
 *  nothing here, and nothing on a fan phone, talks to the league directly.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Play, RefreshCw, Rewind, Save, Square, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { GoalBookedGamesBlock } from './GoalBookedGamesBlock';
import { btn } from './buttons';

/** The moments a league actually has. The server is the source of truth — it
 *  sends `trigger_keys`, the word for a score and the word for a segment — so
 *  nothing here is hockey-shaped and a new league needs no change in this file. */
const momentsFor = (keys, scoreWord, segmentWord) => (keys || []).map((key) => {
    if (key === 'goal') return [key, scoreWord || 'Goal'];
    if (key === 'game_start') return [key, 'Game start'];
    if (key === 'game_end') return [key, 'Game end'];
    const n = key.replace('period_end_', '');
    return [key, `End of ${(segmentWord || 'period').toLowerCase()} ${n}`];
});

const REASONS = {
    automation_off: 'automation off',
    moment_switched_off: 'moment switched off',
    other_team: 'other team scored',
    max_rounds_reached: 'max rounds reached',
    cooldown: 'too soon after the last round',
    debounced: 'ignored, double fire',
    not_seeded: 'game not set up',
    game_end: 'game over',
};

const clockText = (at) => (at ? new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

const NOT_STARTED = new Set(['FUT', 'PRE']);
const OVER = new Set(['OFF', 'FINAL']);

/** Your wording, exactly: watching a game, no game found, or feed unavailable.
 *  Before the puck drops there is no score and no period, so it says "not
 *  started" — never a 0–0 or a "period 0" that would read as real. */
const StatusLine = ({ status, now }) => {
    if (!status) return null;
    if (status.state === 'feed_unavailable') {
        return (
            <span className="text-sm text-rose-600 font-semibold" data-testid="gg-auto-status-text">
                Feed unavailable
                {status.last_ok_at ? ` — last seen ${clockText(status.last_ok_at)}` : ''}
            </span>
        );
    }
    if (status.state === 'no_game') {
        return <span className="text-sm text-amber-600" data-testid="gg-auto-status-text">No game found</span>;
    }
    const since = status.last_checked_at
        ? Math.max(0, Math.round((now - new Date(status.last_checked_at).getTime()) / 1000))
        : null;
    const parts = [`Watching: ${status.game_label}`];
    if (NOT_STARTED.has(status.game_state)) {
        parts.push('not started');
    } else {
        parts.push(status.score || '—');
        if (OVER.has(status.game_state)) parts.push('game over');
        else parts.push(status.period ? `period ${status.period}` : '—');
    }
    if (since !== null) parts.push(`last checked ${since}s ago`);
    return (
        <span className="text-sm" data-testid="gg-auto-status-text">{parts.join(', ')}</span>
    );
};

const gameText = (g) => {
    const time = g.start_time_utc
        ? new Date(g.start_time_utc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        : '';
    return `${g.away_abbrev} at ${g.home_abbrev}${time ? ` · ${time}` : ''} · ${g.state}`;
};

/** "Wed, Oct 14 · 7:30 PM" — so the line above the side buttons names the game
 *  the same way the booked-games table does. */
const gameWhen = (g) => {
    const iso = g?.start_time_utc;
    if (!iso) return g?.game_date || g?.date || '';
    return new Date(iso).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit' });
};

export const GoalAutomationBlock = ({ orgId, promotionId, onReload }) => {
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [games, setGames] = useState([]);
    const [busy, setBusy] = useState(false);
    const [simType, setSimType] = useState('goal');
    const [simSide, setSimSide] = useState('home');
    const [replayDate, setReplayDate] = useState('');
    const [replayGames, setReplayGames] = useState([]);
    const [replayGameId, setReplayGameId] = useState('');
    const [now, setNow] = useState(Date.now());
    // Which game the labels below describe: set by the booked-games block.
    const [focus, setFocus] = useState(null);
    // A booked game's OWN values, so the whole panel below follows the game
    // that is highlighted: its overrides first, the promotion's defaults for
    // anything it does not override. Re-seeded whenever the highlight moves.
    const [bookedDraft, setBookedDraft] = useState(null);
    // Bookings handed back by a per-game save, pushed down so the list and the
    // "customized" tag refresh without a page reload.
    const [bookingsPush, setBookingsPush] = useState(null);

    const focusBooking = focus?.kind === 'booking' ? focus.game : null;
    const focusBookingId = focusBooking?.id || null;

    useEffect(() => {
        if (!focusBooking || !data?.settings) { setBookedDraft(null); return; }
        const base = data.settings;
        const o = focusBooking.overrides || {};
        setBookedDraft({
            team_side: o.team_side || base.team_side || 'either',
            max_rounds: o.max_rounds ?? base.max_rounds,
            gap_seconds: o.gap_seconds ?? base.gap_seconds,
            triggers: { ...(base.triggers || {}), ...(o.triggers || {}) },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusBookingId, data?.settings]);

    // "last checked Ns ago" has to count up on its own, or it lies between polls.
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const load = useCallback(async () => {
        const res = await api.get('/goal-game/automation', {
            params: { organization_id: orgId, promotion_id: promotionId || undefined } });
        setData(res.data);
        setDraft(res.data.settings);
        return res.data;
    }, [orgId, promotionId]);

    const loadGames = useCallback(async (date) => {
        const res = await api.get('/goal-game/automation/games',
            { params: { organization_id: orgId, promotion_id: promotionId || undefined,
                        date: date || undefined } });
        return res.data.games || [];
    }, [orgId, promotionId]);

    useEffect(() => {
        (async () => {
            try {
                const d = await load();
                setGames(await loadGames(d.settings.game_date));
            } catch (_e) { toast.error('Could not load the automation settings'); }
        })();
    }, [load, loadGames]);

    // While the switch is on the page refreshes itself, so rounds that open by
    // themselves appear in the log without a manual reload.
    useEffect(() => {
        if (!data?.settings?.enabled) return undefined;
        const id = setInterval(() => { load().catch(() => { }); }, 10000);
        return () => clearInterval(id);
    }, [data?.settings?.enabled, load]);

    const save = async (patch) => {
        setBusy(true);
        try {
            const res = await api.put('/goal-game/automation', {
                organization_id: orgId, promotion_id: promotionId || null, ...patch });
            setData(res.data);
            setDraft(res.data.settings);
            toast.success('Saved');
            // Other blocks read these settings too — the Star player pick is
            // only offered once the score rounds are on with a game chosen —
            // so the page's own config is refetched, with no manual reload.
            if (onReload) await onReload();
        } catch (_e) { toast.error('Could not save'); } finally { setBusy(false); }
    };

    const post = async (path, body, okText) => {
        setBusy(true);
        try {
            const res = await api.post(path, {
                organization_id: orgId, promotion_id: promotionId || null, ...body });
            if (res.data.settings) { setData(res.data); setDraft(res.data.settings); }
            else await load();
            if (okText) toast.success(okText);
            if (onReload) await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail === 'game_not_found'
                ? 'That game was not found in the feed' : 'Could not do that');
        } finally { setBusy(false); }
    };

    if (!data || !draft) {
        return <div className="pt-4"><Loader2 className="animate-spin text-slate-400" size={18} /></div>;
    }

    const s = data.settings;
    const game = data.game;

    // WHOSE GOALS OPEN A ROUND names the teams of the game you are LOOKING at,
    // not whatever the promotion happened to be saved with. In order: a game
    // highlighted below (a calendar tick, or a booking whose Settings are
    // open), then the game chosen in the picker above, then the game the
    // promotion is following now. Labels only — the saved value stays a side.
    // Only a game the operator has actually CHANGED counts as "chosen"; the
    // saved one is still the promotion's default and reads as such.
    const pickerGame = draft.game_id && draft.game_id !== s.game_id
        ? games.find((g) => g.id === draft.game_id) || null : null;
    const shown = focus?.game || pickerGame || game;
    const homeAb = shown?.home_abbrev || '—';
    const awayAb = shown?.away_abbrev || '—';
    // A booked game keeps its side on its own Settings, so the buttons here
    // show that game's side read-only rather than pretending to own it.
    // Simulate keeps naming the game the promotion is actually following —
    // highlighting a game below must not change what a simulated goal does.
    const liveHome = game?.home_abbrev || '—';
    const liveAway = game?.away_abbrev || '—';
    const booked = focus?.kind === 'booking' ? focus.game : null;
    // The whole panel below reads ONE object: the highlighted booked game's own
    // values when a booking is selected, the promotion's draft otherwise.
    const view = booked && bookedDraft ? bookedDraft : draft;
    const shownSide = view.team_side;
    const field = (k, v) => (booked && bookedDraft
        ? setBookedDraft((p) => ({ ...p, [k]: v }))
        : setDraft((p) => ({ ...p, [k]: v })));
    const moment = (k, v) => (booked && bookedDraft
        ? setBookedDraft((p) => ({ ...p, triggers: { ...p.triggers, [k]: v } }))
        : setDraft((p) => ({ ...p, triggers: { ...p.triggers, [k]: v } })));

    // Save automation writes to whatever is highlighted: a booked game's own
    // settings, or the promotion's defaults when nothing is highlighted.
    const saveBooking = async () => {
        setBusy(true);
        try {
            const res = await api.patch(`/goal-game/bookings/${booked.id}`, {
                organization_id: orgId,
                team_side: bookedDraft.team_side,
                triggers: bookedDraft.triggers,
                max_rounds: Number(bookedDraft.max_rounds),
                gap_seconds: Number(bookedDraft.gap_seconds),
            });
            const rows = res.data.bookings || [];
            setBookingsPush(rows);
            const fresh = rows.find((b) => b.id === booked.id);
            if (fresh) setFocus({ kind: 'booking', game: fresh });
            toast.success(`Saved for ${booked.label} only`);
        } catch (_e) { toast.error('Could not save this game'); }
        finally { setBusy(false); }
    };


    return (
        <div className="space-y-5 pt-3" data-testid="gg-automation">
            {/* master switch */}
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <button
                    type="button" disabled={busy}
                    onClick={() => save({ enabled: !s.enabled })}
                    data-testid="gg-auto-master"
                    className={s.enabled ? btn.danger : btn.primary}
                >
                    {busy ? <Loader2 className="animate-spin" size={15} /> : <Zap size={15} />}
                    {s.enabled ? 'Switch automation off' : 'Switch automation on'}
                </button>
                <span className="flex flex-wrap items-center gap-x-2 text-sm" data-testid="gg-auto-status">
                    {s.enabled
                        ? <strong className="text-emerald-600">ON</strong>
                        : <strong className="text-slate-500">OFF</strong>}
                    <StatusLine status={data.status} now={now} />
                    <span className="text-slate-500">· Rounds opened {s.rounds_fired} of {s.max_rounds}</span>
                </span>
                <button type="button" disabled={busy} className={btn.smallSecondary}
                        data-testid="gg-auto-tick"
                        onClick={() => post('/goal-game/automation/tick', {}, 'Feed checked')}>
                    <RefreshCw size={13} /> Check the feed now
                </button>
            </div>

            {/* A — the game */}
            <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Tonight's game ({data.tonight_date})
                </p>
                <div className="flex flex-wrap items-end gap-2">
                    <label className="block grow min-w-[260px]">
                        <span className="block text-xs text-slate-500 mb-1">Pick the game</span>
                        <select
                            value={draft.game_id || ''} data-testid="gg-auto-game"
                            onChange={(e) => {
                                const g = games.find((x) => x.id === e.target.value);
                                setDraft((p) => ({
                                    ...p, game_id: e.target.value || null,
                                    game_date: g?.date || data.tonight_date,
                                    game_label: g?.label || null,
                                }));
                            }}
                            className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                        >
                            <option value="">— no game chosen —</option>
                            {games.map((g) => (
                                <option key={g.id} value={g.id}>{gameText(g)}</option>
                            ))}
                        </select>
                    </label>
                    <button type="button" disabled={busy} className={btn.secondary}
                            data-testid="gg-auto-refresh-games"
                            onClick={async () => {
                                try {
                                    setGames(await loadGames(data.tonight_date));
                                    toast.success('Game list refreshed');
                                } catch (_e) { toast.error('Could not reach the feed'); }
                            }}>
                        <RefreshCw size={15} /> Refresh list
                    </button>
                </div>
                {games.length === 0 && (
                    <p className="text-xs text-amber-600" data-testid="gg-auto-no-games">
                        No games today — the feed has nothing for {data.tonight_date}.
                    </p>
                )}
                <label className="block max-w-[200px]">
                    <span className="block text-xs text-slate-500 mb-1">
                        Shortcut — follow a team (optional)
                    </span>
                    <input
                        value={draft.follow_team || ''} maxLength={3}
                        onChange={(e) => field('follow_team', e.target.value.toUpperCase())}
                        placeholder="e.g. MTL" data-testid="gg-auto-follow-team"
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm uppercase"
                    />
                </label>
                <p className="text-xs text-slate-500">
                    With a team here and no game chosen, tonight's game for that team is used.
                </p>
            </div>

            {/* Games booked ahead of time. Unchanged above: the dropdown, the
                shortcut and the master switch all work exactly as before. */}
            <GoalBookedGamesBlock orgId={orgId} promotionId={promotionId} auto={data}
                                  onFocusGame={setFocus} selectedBookingId={focusBookingId}
                                  pushedBookings={bookingsPush} />

            {/* B — whose goals */}
            <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Whose {(data.score_word || 'Goal').toLowerCase()}s open a round
                </p>
                <p className="text-xs text-slate-500" data-testid="gg-auto-side-context">
                    {booked
                        ? `Showing ${booked.label}, ${gameWhen(booked)} — saved for this game only`
                        : shown
                            ? (focus || pickerGame
                                ? `Showing ${shown.label || `${awayAb} at ${homeAb}`}, ${gameWhen(shown)}`
                                : `Default for the game this promotion is following now: ${shown.label || `${awayAb} at ${homeAb}`}`)
                            : 'Default for the game this promotion is following now: no game chosen'}
                </p>
                <div className="flex flex-wrap gap-2">
                    {[['home', `Home: ${homeAb}`], ['away', `Away: ${awayAb}`], ['either', 'Either team']]
                        .map(([value, label]) => (
                            <button
                                key={value} type="button"
                                onClick={() => field('team_side', value)}
                                data-testid={`gg-auto-side-${value}`}
                                className={shownSide === value ? btn.smallPrimary : btn.smallSecondary}
                            >
                                {label}
                            </button>
                        ))}
                </div>
            </div>

            {/* moments */}
            <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Moments that open a round
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                    {momentsFor(data.trigger_keys, data.score_word, data.segment_word).map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox" checked={!!view.triggers?.[key]}
                                onChange={(e) => moment(key, e.target.checked)}
                                data-testid={`gg-auto-moment-${key}`}
                                className="h-4 w-4 rounded border-slate-300"
                            />
                            {label}
                        </label>
                    ))}
                </div>
            </div>

            {/* safety */}
            <div className="flex flex-wrap items-end gap-3">
                <label className="block max-w-[180px]">
                    <span className="block text-xs text-slate-500 mb-1">
                        Max rounds per game ({data.max_rounds_min}–{data.max_rounds_max})
                    </span>
                    <input
                        type="number" min={data.max_rounds_min} max={data.max_rounds_max}
                        value={view.max_rounds}
                        onChange={(e) => field('max_rounds', e.target.value)}
                        data-testid="gg-auto-max-rounds"
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm tabular-nums"
                    />
                </label>
                <label className="block max-w-[180px]">
                    <span className="block text-xs text-slate-500 mb-1">
                        Minimum gap between rounds ({data.gap_min}–{data.gap_max}s)
                    </span>
                    <input
                        type="number" min={data.gap_min} max={data.gap_max}
                        value={view.gap_seconds}
                        onChange={(e) => field('gap_seconds', e.target.value)}
                        data-testid="gg-auto-gap-seconds"
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm tabular-nums"
                    />
                </label>
                <button type="button" disabled={busy} className={btn.warning}
                        data-testid="gg-auto-reset-rounds"
                        onClick={() => post('/goal-game/automation/reset-rounds', {}, 'Round count reset')}>
                    Reset the round count
                </button>
                <button type="button" disabled={busy} className={btn.primary}
                        data-testid="gg-auto-save"
                        onClick={() => (booked && bookedDraft ? saveBooking() : save({
                            game_id: draft.game_id, game_date: draft.game_date,
                            game_label: draft.game_label, team_side: draft.team_side,
                            follow_team: draft.follow_team, max_rounds: draft.max_rounds,
                            gap_seconds: draft.gap_seconds, triggers: draft.triggers,
                        }))}>
                    {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                    {booked ? 'Save this game' : 'Save automation'}
                </button>
            </div>
            <p className="text-xs text-slate-500">
                Safety rules, always on: switching on ignores everything that already happened in
                the game; at most {view.max_rounds} rounds per game; at least {view.gap_seconds}s
                between automatic rounds; the feed is checked every {data.poll_seconds}s and backs
                off by itself if it fails; game end switches the master switch off. The manual
                GOAL! button and the trigger card keep working throughout.
            </p>

            {/* simulate */}
            <div className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Simulate one moment
                </p>
                <div className="flex flex-wrap items-end gap-2">
                    <select value={simType} onChange={(e) => setSimType(e.target.value)}
                            data-testid="gg-auto-sim-type"
                            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                        {momentsFor(data.trigger_keys, data.score_word, data.segment_word)
                            .map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                    <select value={simSide} onChange={(e) => setSimSide(e.target.value)}
                            data-testid="gg-auto-sim-side"
                            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                        <option value="home">Home: {liveHome}</option>
                        <option value="away">Away: {liveAway}</option>
                    </select>
                    <button type="button" disabled={busy} className={btn.secondary}
                            data-testid="gg-auto-simulate"
                            onClick={() => post('/goal-game/automation/simulate',
                                { event_type: simType, team_side: simSide }, 'Moment simulated')}>
                        <Play size={15} /> Simulate event
                    </button>
                </div>
                <p className="text-xs text-slate-500">
                    Runs through the real rules — the switches, the team choice, the round cap and
                    the gap all apply, so the log shows exactly what a real moment would do.
                </p>
            </div>

            {/* replay — NHL only: it is built from the NHL feed's own per-goal
                rows, which no other league gives us. */}
            {data.replay_available !== false && (
            <div className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3"
                 data-testid="gg-auto-replay-block">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Replay a real past game
                </p>
                {s.replay ? (
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm" data-testid="gg-auto-replay-status">
                            Replaying {s.replay.label} — moment {s.replay.cursor} of {s.replay.total}
                        </span>
                        <button type="button" disabled={busy} className={btn.warning}
                                data-testid="gg-auto-replay-stop"
                                onClick={() => post('/goal-game/automation/replay/stop', {}, 'Replay stopped')}>
                            <Square size={15} /> Stop replay
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-end gap-2">
                        <label className="block">
                            <span className="block text-xs text-slate-500 mb-1">Date of the game</span>
                            <input
                                type="date" value={replayDate} data-testid="gg-auto-replay-date"
                                onChange={async (e) => {
                                    setReplayDate(e.target.value);
                                    setReplayGameId('');
                                    try { setReplayGames(await loadGames(e.target.value)); }
                                    catch (_err) { toast.error('Could not reach the feed'); }
                                }}
                                className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block grow min-w-[240px]">
                            <span className="block text-xs text-slate-500 mb-1">Finished game</span>
                            <select value={replayGameId} data-testid="gg-auto-replay-game"
                                    onChange={(e) => setReplayGameId(e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                                <option value="">— pick a game —</option>
                                {replayGames.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {gameText(g)} · {g.away_score}–{g.home_score}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button type="button" disabled={busy || !replayDate || !replayGameId}
                                className={btn.secondary} data-testid="gg-auto-replay-start"
                                onClick={() => post('/goal-game/automation/replay/start',
                                    { date: replayDate, game_id: replayGameId }, 'Replay started')}>
                            <Rewind size={15} /> Start replay
                        </button>
                    </div>
                )}
                <p className="text-xs text-slate-500">
                    One real moment every {data.poll_seconds}s, through the same rules. The master
                    switch must be on, and the replay's game end switches it off just like a real one.
                </p>
            </div>
            )}

            {/* log */}
            <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Last {(data.log || []).length} events
                </p>
                {(data.log || []).length === 0 ? (
                    <p className="text-sm text-slate-500" data-testid="gg-auto-log-empty">
                        Nothing yet.
                    </p>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                        <table className="w-full text-sm" data-testid="gg-auto-log">
                            <tbody>
                                {data.log.map((row) => (
                                    <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500 tabular-nums">
                                            {new Date(row.at).toLocaleTimeString()}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500">
                                            {row.source}
                                        </td>
                                        <td className="px-3 py-2">{row.event_label}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-xs tabular-nums">
                                            {row.score || '—'}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {row.fired
                                                ? <span className="text-emerald-600 font-semibold">round opened</span>
                                                : <span className="text-slate-500">
                                                    no round{row.reason ? ` — ${REASONS[row.reason] || row.reason}` : ''}
                                                  </span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <button type="button" disabled={busy} className={btn.smallSecondary}
                        data-testid="gg-auto-clear-log"
                        onClick={() => post('/goal-game/automation/clear-log', {}, 'Log cleared')}>
                    Clear the log
                </button>
            </div>
        </div>
    );
};

export default GoalAutomationBlock;
